import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Breadcrumb from "../../components/ui/Breadcrumb";
import PatientInfoForm from "./components/PatientInfoForm";
import PhotoUploadZone from "./components/PhotoUploadZone";
import Icon from "../../components/AppIcon";
import { inferImage } from "../../lib/api";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { motion, AnimatePresence } from "framer-motion";

// ** VIDEO SCAN **
import VideoScanInterface from "./components/VideoScanInterface";

/* ─── shared surface style ─────────────────────────────────── */
const surface = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
};

const NewAnalysis = () => {
  const navigate = useNavigate();

  /* ----------------------------------
     FORM STATES
  ---------------------------------- */
  const [patientName, setPatientName] = useState("");
  const [notes, setNotes] = useState("");
  const [analysisDate, setAnalysisDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [uploadedFile, setUploadedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  /* ----------------------------------
     IMAGE CROP
  ---------------------------------- */
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [isSavingCrop, setIsSavingCrop] = useState(false);
  const imgRef = useRef(null);

  /* ----------------------------------
     STATES
  ---------------------------------- */
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);

  const [useVideoMode, setUseVideoMode] = useState(false);
  const [errors, setErrors] = useState({});

  /* ----------------------------------
     VALIDATION
  ---------------------------------- */
  const validateForm = () => {
    const newErrors = {};

    if (!patientName.trim()) newErrors.patientName = "Patient name is required";
    if (!analysisDate) newErrors.analysisDate = "Analysis date is required";

    if (!useVideoMode && !uploadedFile)
      newErrors.file = "Please upload a patient photo";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ----------------------------------
     IMAGE PREVIEW
  ---------------------------------- */
  const handleFileSelect = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  /* ----------------------------------
     CROP IMAGE
  ---------------------------------- */
  const createCroppedImage = async (image, crop) => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) =>
          resolve(new File([blob], "cropped_image.jpg", { type: "image/jpeg" })),
        "image/jpeg",
        0.95
      );
    });
  };

  const handleCropConfirm = async () => {
    if (!completedCrop || !imgRef.current) {
      setShowCropModal(false);
      return;
    }

    setIsSavingCrop(true);

    try {
      const newFile = await createCroppedImage(imgRef.current, completedCrop);
      const newPreviewUrl = URL.createObjectURL(newFile);

      setUploadedFile(newFile);
      setPreview(newPreviewUrl);
      setShowCropModal(false);

      setCrop(undefined);
      setCompletedCrop(null);
    } catch (e) {
      console.error("Crop error", e);
    } finally {
      setIsSavingCrop(false);
    }
  };

  /* ----------------------------------
     IMAGE ANALYSIS
  ---------------------------------- */
  const handleStartImageAnalysis = async () => {
    if (!validateForm()) return;
    if (useVideoMode) return;

    try {
      setIsAnalyzing(true);
      setAnalysisProgress(10);

      const apiResponse = await inferImage({
        imageFile: uploadedFile,
        patientName,
      });

      for (let i = 10; i <= 100; i += 10) {
        await new Promise((r) => setTimeout(r, 150));
        setAnalysisProgress(i);
      }

      await new Promise((r) => setTimeout(r, 400));
      setIsFinishing(true);
      await new Promise((r) => setTimeout(r, 450));

      navigate("/analysis-result", {
        state: { ...apiResponse, patientName, notes, analysisDate },
      });
    } catch (err) {
      console.error("Analysis Error:", err);
      setErrors({ api: err.message || "Analysis failed, please try again." });
      setIsAnalyzing(false);
    }
  };

  /* ----------------------------------
     VIDEO ANALYSIS → FIXED ROUTE
  ---------------------------------- */
  const handleVideoAnalysisComplete = (result) =>
    navigate(`/analysis-result-video/${result.jobId}`, {
      state: {
        jobId: result.jobId,
        patientName,
        notes,
        analysisDate,
        videoKey: result.videoKey,
      },
    });

  /* ----------------------------------
     RENDER
  ---------------------------------- */
  const breadcrumbItems = [
    { label: "Dashboard", path: "/dashboard" },
    { label: "New Analysis", path: "/new-analysis" },
  ];

  const steps = [
    { done: patientName?.trim() },
    { done: useVideoMode || uploadedFile },
    { done: patientName?.trim() && (useVideoMode || uploadedFile) },
  ];

  const completion = Math.round(
    (steps.filter((s) => s.done).length / steps.length) * 100
  );

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      style={{ background: "#060d1c" }}
    >
      {/* CSS grid */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(59,130,246,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.05) 1px, transparent 1px)",
          backgroundSize: "52px 52px",
        }}
      />

      {/* Ambient glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute"
          style={{
            top: "-120px",
            left: "-60px",
            width: "600px",
            height: "600px",
            background:
              "radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute"
          style={{
            bottom: "-80px",
            right: "-80px",
            width: "500px",
            height: "500px",
            background:
              "radial-gradient(circle, rgba(6,182,212,0.07) 0%, transparent 70%)",
          }}
        />
      </div>

      {/* Content */}
      <motion.div
        className="relative z-10 min-h-screen overflow-y-auto pb-24"
        animate={{
          opacity: isFinishing ? 0 : 1,
          filter: isFinishing ? "blur(12px)" : "blur(0px)",
          scale: isFinishing ? 0.98 : 1,
        }}
        transition={{ duration: 0.45 }}
      >
        <Header />

        {/* VIDEO MODE */}
        {useVideoMode && (
          <VideoScanInterface
            patientName={patientName}
            notes={notes}
            analysisDate={analysisDate}
            onComplete={handleVideoAnalysisComplete}
            onCancel={() => setUseVideoMode(false)}
          />
        )}

        {/* IMAGE MODE */}
        {!useVideoMode && (
          <main className="pt-10 px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              {/* Header card */}
              <div
                className="mt-8 rounded-2xl p-7 shadow-xl"
                style={surface}
              >
                {/* Accent top line */}
                <div
                  className="absolute top-0 left-0 right-0 h-[1px] rounded-t-2xl"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, #3B82F6 40%, #06B6D4 60%, transparent)",
                    position: "relative",
                    marginBottom: "-1px",
                  }}
                />

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(59,130,246,0.22), rgba(6,182,212,0.14))",
                          border: "1px solid rgba(59,130,246,0.3)",
                        }}
                      >
                        <Icon name="Microscope" size={17} color="#60A5FA" />
                      </div>
                      <h1
                        className="text-2xl font-light tracking-tight"
                        style={{ color: "#E2E8F0" }}
                      >
                        New Hair Transplant Analysis
                      </h1>
                    </div>
                    <p className="text-sm ml-12" style={{ color: "#64748B" }}>
                      Enter patient details and upload the scalp image to begin
                      AI-powered graft analysis.
                    </p>
                  </div>

                  <div className="shrink-0 text-right">
                    <span
                      className="text-2xl font-light"
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        color: "#60A5FA",
                      }}
                    >
                      {completion}%
                    </span>
                    <p
                      className="text-[11px] mt-0.5"
                      style={{ color: "rgba(255,255,255,0.25)" }}
                    >
                      complete
                    </p>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-6">
                  <div
                    className="w-full h-1 rounded-full overflow-hidden"
                    style={{ background: "rgba(255,255,255,0.07)" }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${completion}%`,
                        background:
                          "linear-gradient(90deg, #2563EB, #06B6D4)",
                      }}
                    />
                  </div>

                  {/* Step dots */}
                  <div className="flex justify-between mt-3">
                    {[
                      { n: "01", label: "Patient Info" },
                      { n: "02", label: "Upload" },
                      { n: "03", label: "Run Analysis" },
                    ].map(({ n, label }, i) => {
                      const done = steps[i]?.done;
                      return (
                        <div
                          key={n}
                          className="flex items-center gap-1.5"
                        >
                          <span
                            className="text-[10px] font-semibold"
                            style={{
                              fontFamily: "'JetBrains Mono', monospace",
                              color: done
                                ? "#06B6D4"
                                : "rgba(255,255,255,0.2)",
                            }}
                          >
                            {n}
                          </span>
                          <span
                            className="text-[11px]"
                            style={{
                              color: done
                                ? "rgba(255,255,255,0.5)"
                                : "rgba(255,255,255,0.18)",
                            }}
                          >
                            {label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* ── STEP 1: Patient Information ── */}
              <div className="mt-5 rounded-2xl shadow-xl overflow-hidden" style={surface}>
                <div
                  className="px-6 py-4 flex items-center gap-3"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{
                      background: "rgba(59,130,246,0.18)",
                      border: "1px solid rgba(59,130,246,0.28)",
                      color: "#93C5FD",
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    01
                  </div>
                  <h2
                    className="text-sm font-semibold tracking-wide"
                    style={{ color: "#CBD5E1" }}
                  >
                    Patient Information
                  </h2>
                </div>

                <div className="p-6" style={{ color: "white" }}>
                  <PatientInfoForm
                    patientName={patientName}
                    setPatientName={setPatientName}
                    notes={notes}
                    setNotes={setNotes}
                    analysisDate={analysisDate}
                    setAnalysisDate={setAnalysisDate}
                    errors={errors}
                  />
                </div>
              </div>

              {/* ── Photo Guidelines ── */}
              <div className="mt-5 rounded-2xl shadow-xl overflow-hidden" style={surface}>
                <div
                  className="px-6 py-4 flex items-center gap-3"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      background: "rgba(245,158,11,0.12)",
                      border: "1px solid rgba(245,158,11,0.22)",
                    }}
                  >
                    <Icon name="Info" size={13} color="#FCD34D" />
                  </div>
                  <h2
                    className="text-sm font-semibold tracking-wide"
                    style={{ color: "#CBD5E1" }}
                  >
                    Photo Guidelines
                  </h2>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      { icon: "Sun", text: "Even lighting, no shadows" },
                      { icon: "Eye", text: "Full graft area visible" },
                      { icon: "ZoomIn", text: "Sharp, no blur" },
                      { icon: "Crop", text: "Crop unrelated areas" },
                      { icon: "Aperture", text: "Avoid scalp reflections" },
                      { icon: "Image", text: "JPG / PNG · max 10MB" },
                    ].map(({ icon, text }) => (
                      <div
                        key={text}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl"
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.07)",
                        }}
                      >
                        <Icon name={icon} size={14} color="#64748B" />
                        <span
                          className="text-xs leading-snug"
                          style={{ color: "#94A3B8" }}
                        >
                          {text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── STEP 2: Upload / Analysis Method ── */}
              <div className="mt-5 rounded-2xl shadow-xl overflow-hidden" style={surface}>
                <div
                  className="px-6 py-4 flex items-center gap-3"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{
                      background: "rgba(59,130,246,0.18)",
                      border: "1px solid rgba(59,130,246,0.28)",
                      color: "#93C5FD",
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    02
                  </div>
                  <h2
                    className="text-sm font-semibold tracking-wide"
                    style={{ color: "#CBD5E1" }}
                  >
                    Choose Analysis Method
                  </h2>
                </div>

                <div className="p-6">
                  <PhotoUploadZone
                    uploadedFile={uploadedFile}
                    setUploadedFile={setUploadedFile}
                    uploadProgress={uploadProgress}
                    setUploadProgress={setUploadProgress}
                    isUploading={isUploading}
                    setIsUploading={setIsUploading}
                    errors={errors}
                    setErrors={setErrors}
                    onFileSelect={handleFileSelect}
                    onVideoModeSelect={() => setUseVideoMode(true)}
                  />

                  {/* Crop button */}
                  {preview && uploadedFile?.type?.startsWith("image/") && (
                    <div className="flex justify-end mt-5">
                      <button
                        onClick={() => setShowCropModal(true)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-all duration-150"
                        style={{
                          background: "rgba(255,255,255,0.06)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          color: "rgba(255,255,255,0.6)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                          e.currentTarget.style.color = "#fff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                          e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                        }}
                      >
                        <Icon name="Crop" size={14} />
                        Crop Image
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ── STEP 3: Start Analysis ── */}
              {!useVideoMode && (patientName?.trim() || uploadedFile) && (
                <div className="mt-5 rounded-2xl shadow-xl overflow-hidden" style={surface}>
                  <div
                    className="px-6 py-4 flex items-center gap-3"
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
                  >
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{
                        background: "rgba(59,130,246,0.18)",
                        border: "1px solid rgba(59,130,246,0.28)",
                        color: "#93C5FD",
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    >
                      03
                    </div>
                    <h2
                      className="text-sm font-semibold tracking-wide"
                      style={{ color: "#CBD5E1" }}
                    >
                      Run Analysis
                    </h2>
                  </div>

                  <div className="p-8 flex flex-col items-center gap-4">
                    {/* Progress bar during analysis */}
                    {isAnalyzing && (
                      <div className="w-full max-w-sm mb-2">
                        <div className="flex justify-between text-xs mb-1.5"
                          style={{ color: "rgba(255,255,255,0.4)" }}>
                          <span>Processing image...</span>
                          <span style={{ color: "#06B6D4", fontFamily: "'JetBrains Mono', monospace" }}>
                            {analysisProgress}%
                          </span>
                        </div>
                        <div
                          className="w-full h-1 rounded-full overflow-hidden"
                          style={{ background: "rgba(255,255,255,0.07)" }}
                        >
                          <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{
                              width: `${analysisProgress}%`,
                              background: "linear-gradient(90deg, #2563EB, #06B6D4)",
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <motion.button
                      onClick={handleStartImageAnalysis}
                      disabled={isAnalyzing}
                      className="flex items-center gap-3 px-10 py-4 rounded-xl font-medium text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        background: isAnalyzing
                          ? "rgba(255,255,255,0.06)"
                          : "linear-gradient(135deg, #1D4ED8, #0EA5E9)",
                        boxShadow: isAnalyzing
                          ? "none"
                          : "0 8px 28px rgba(29,78,216,0.45)",
                        fontSize: "0.925rem",
                      }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {isAnalyzing ? (
                        <>
                          <Icon name="Loader2" size={20} className="animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Icon name="Zap" size={20} />
                          Run AI Analysis
                        </>
                      )}
                    </motion.button>

                  </div>
                </div>
              )}

              {/* API Error */}
              {errors.api && (
                <div
                  className="mt-6 rounded-xl p-4"
                  style={{
                    background: "rgba(220,38,38,0.1)",
                    border: "1px solid rgba(220,38,38,0.3)",
                  }}
                >
                  <p className="font-semibold text-sm mb-1" style={{ color: "#FCA5A5" }}>
                    Analysis Error
                  </p>
                  <p className="text-sm" style={{ color: "#F87171" }}>
                    {errors.api}
                  </p>
                </div>
              )}
            </div>
          </main>
        )}
      </motion.div>

      {/* CROP MODAL */}
      <AnimatePresence>
        {showCropModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(12px)" }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-5xl rounded-2xl flex flex-col shadow-2xl overflow-hidden"
              style={{
                background: "#0a1020",
                border: "1px solid rgba(255,255,255,0.1)",
                maxHeight: "95vh",
              }}
            >
              {/* Modal header */}
              <div
                className="p-4 flex justify-between items-center shrink-0"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
              >
                <h3
                  className="text-base font-semibold flex items-center gap-2"
                  style={{ color: "#E2E8F0" }}
                >
                  <Icon name="Crop" size={18} color="#60A5FA" />
                  Crop Image
                </h3>
                <p
                  className="text-xs hidden sm:block"
                  style={{ color: "rgba(255,255,255,0.3)" }}
                >
                  Draw a rectangle to select crop area
                </p>
              </div>

              {/* Crop area */}
              <div
                className="flex-1 overflow-hidden relative flex items-center justify-center p-4"
                style={{ background: "rgba(0,0,0,0.4)" }}
              >
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  className="max-w-full"
                >
                  <img
                    ref={imgRef}
                    src={preview}
                    alt="Crop Preview"
                    style={{
                      maxHeight: "70vh",
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                </ReactCrop>
              </div>

              {/* Modal footer */}
              <div
                className="p-4 flex justify-end gap-3 shrink-0"
                style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
              >
                <button
                  onClick={() => setShowCropModal(false)}
                  className="px-4 py-2 rounded-lg text-sm transition-all"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    color: "rgba(255,255,255,0.6)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                  }}
                >
                  Cancel
                </button>

                <button
                  onClick={handleCropConfirm}
                  disabled={!completedCrop?.width || !completedCrop?.height}
                  className="px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background:
                      !completedCrop?.width || !completedCrop?.height
                        ? "rgba(255,255,255,0.05)"
                        : "linear-gradient(135deg, #1D4ED8, #0EA5E9)",
                    boxShadow:
                      !completedCrop?.width || !completedCrop?.height
                        ? "none"
                        : "0 4px 16px rgba(29,78,216,0.4)",
                  }}
                >
                  {isSavingCrop ? "Saving..." : "Save Crop"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewAnalysis;
